import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { animate, motion, useInView, useMotionValue, useReducedMotion, useTransform } from "motion/react";
import { ArrowRight, ArrowUpRight, Prohibit, ShieldCheck } from "@phosphor-icons/react";
import { guides, problemCategories } from "../../data/content.js";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, ScrubStatement, Seo } from "../../kit.jsx";
import { GuideGlyph } from "./Glyphs.jsx";
import { GUIDE_ART, PROBLEM_EXTRA } from "./learnData.js";
import LearnNotFound from "./LearnNotFound.jsx";
import { ProblemIcon } from "./ProblemIcon.jsx";
import "./learn.css";

function RevealItem({ children, delay = 0, from = "below" }) {
  const reduceMotion = useReducedMotion();
  const start = from === "left" ? { opacity: 0, x: -28 } : { opacity: 0, y: 24 };
  return (
    <motion.li
      initial={reduceMotion ? false : start}
      whileInView={reduceMotion ? undefined : { opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.8, delay, ease: EASE_OUT }}
    >
      {children}
    </motion.li>
  );
}

// Counts up figures such as "251M" or "1.3B". Figures written as a ratio ("1 in 7") stay still.
function ScaleFigure({ value }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const match = /^([\d.]+)([A-Za-z]*)$/.exec(value);
  const target = match ? parseFloat(match[1]) : 0;
  const decimals = match && match[1].includes(".") ? match[1].split(".")[1].length : 0;
  const progress = useMotionValue(0);
  const text = useTransform(progress, (latest) => `${latest.toFixed(decimals)}${match ? match[2] : ""}`);

  useEffect(() => {
    if (!match || !inView || reduceMotion) return undefined;
    const controls = animate(progress, target, { duration: 1.3, ease: EASE_OUT });
    return () => controls.stop();
  }, [inView, match, progress, reduceMotion, target]);

  if (!match || reduceMotion) return <strong className="lb-figure" ref={ref}>{value}</strong>;
  return (
    <strong className="lb-figure" ref={ref}>
      <span className="sr-only">{value}</span>
      <motion.span aria-hidden="true">{text}</motion.span>
    </strong>
  );
}

function ProblemStory({ problem }) {
  const reduceMotion = useReducedMotion();
  const extra = PROBLEM_EXTRA[problem.slug];
  const others = problemCategories.filter((item) => item.slug !== problem.slug);
  const readNext = extra.guides.map((slug) => guides.find((guide) => guide.slug === slug)).filter(Boolean);
  const appear = (delay) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.9, delay, ease: EASE_OUT },
  });

  return (
    <PageFrame className={`rd-page learn-page lb-problem ${extra.safety ? "lb-problem--safety" : ""}`}>
      <Seo title={`${problem.title} Problems for Student Projects`} path={`/problems/${problem.slug}`} description={problem.summary} />
      <main>
        <header className="lb-ph">
          <motion.nav className="lb-gh__crumb" aria-label="Breadcrumb" {...appear(0)}>
            <Link to="/problems">Problems</Link>
            <span aria-hidden="true">/</span>
            <span>{extra.label}</span>
          </motion.nav>
          <div className="lb-ph__title">
            <motion.span
              className="lb-ph__icon"
              initial={reduceMotion ? false : { scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE_OUT }}
            >
              <ProblemIcon name={extra.icon} weight="regular" />
            </motion.span>
            <MaskWords as="h1" text={problem.title} onMount delay={0.1} />
          </div>
          <div className="lb-ph__row">
            <div className="lb-ph__copy">
              <motion.p className="lb-ph__lead" {...appear(0.35)}>{problem.summary}</motion.p>
              <motion.p {...appear(0.45)}>{problem.gridRole}</motion.p>
              <motion.div className="gh-actions" {...appear(0.55)}>
                <a className="gh-btn gh-btn--fill" href="#start">
                  <span className="roll" data-label="Where to start"><span>Where to start</span></span>
                </a>
                <a className="gh-btn gh-btn--ghost" href="#avoid">
                  <span className="roll" data-label="What to avoid"><span>What to avoid</span></span>
                </a>
              </motion.div>
            </div>
            <ClipPhoto className="lb-ph__photo" src={extra.photo} alt={extra.alt} caption={extra.caption} priority sizes="(max-width: 900px) 100vw, 50vw" />
          </div>
        </header>

        <section className="lb-issue" aria-labelledby="lb-issue-title">
          <div className="lb-issue__head">
            <span className="lb-mono">What the problem is</span>
            <MaskWords id="lb-issue-title" text="Bigger than the first symptom." />
            <p>These are the reasons the problem is hard. Read them before you pick a response.</p>
          </div>
          <ul className="lb-issue__list">
            {problem.stakes.map((stake, index) => (
              <RevealItem key={stake} delay={index * 0.06}>{stake}</RevealItem>
            ))}
          </ul>
        </section>

        <section className="lb-who" aria-labelledby="lb-who-title">
          <span className="lb-mono">Who it affects</span>
          <h2 id="lb-who-title" className="sr-only">Who it affects</h2>
          <div className="lb-who__row">
            <ScaleFigure value={problem.scale} />
            <div className="lb-who__copy">
              <p>{problem.scaleLabel.charAt(0).toUpperCase() + problem.scaleLabel.slice(1)}.</p>
              <p className="lb-who__note">This global figure gives a sense of scale. A useful student project still starts with one place and people you can reach.</p>
              <a className="gh-link" href={problem.sourceUrl} target="_blank" rel="noreferrer">
                Source: {problem.sourceName} <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <section className="lb-start" id="start" aria-labelledby="lb-start-title">
          <span className="lb-mono">A question to start with</span>
          <h2 id="lb-start-title" className="sr-only">A question to start with</h2>
          <ScrubStatement text={extra.question} />
          <div className="lb-start__grid">
            <div>
              <h3>How to begin</h3>
              <p>{extra.begin}</p>
            </div>
            <div>
              <h3>Before you build</h3>
              <p>Write down what you observed, what people told you and what you are assuming. Choose one assumption to check before making a prototype.</p>
            </div>
          </div>
          <div className="lb-start__contribution">
            <span className="lb-chip">A responsible starting point</span>
            <p>{problem.contribution}</p>
          </div>
        </section>

        <section className="lb-ideas" aria-labelledby="lb-ideas-title">
          <div className="lb-ideas__head">
            <MaskWords id="lb-ideas-title" text="Project ideas you can try." />
            <p>Small enough to test in two weeks. Pick one, then narrow it to your own school or street.</p>
          </div>
          <ol className="lb-ideas__list">
            {extra.ideas.map((idea, index) => (
              <RevealItem key={idea} delay={index * 0.08} from="left">
                <span className="lb-ideas__num" aria-hidden="true">{String.fromCharCode(65 + index)}</span>
                <p>{idea}</p>
              </RevealItem>
            ))}
          </ol>
          {extra.example && (
            <Link className="gh-link lb-ideas__example" to={extra.example.to}>
              {extra.example.label} <ArrowRight weight="bold" aria-hidden="true" />
            </Link>
          )}
        </section>

        <section className="lb-avoid" id="avoid" data-nav-tone="dark" aria-labelledby="lb-avoid-title">
          <div className="lb-avoid__inner">
            <div className="lb-avoid__head">
              <span className="lb-mono lb-mono--dark">{extra.safety ? "Safety boundaries" : "What to avoid"}</span>
              <MaskWords id="lb-avoid-title" text={extra.safety ? "Where a student project must stop." : "Mistakes that make a project worse."} />
              {extra.safety && (
                <p className="lb-avoid__lead">
                  Health problems can involve safety, privacy, stigma and clinical knowledge. A student project should never present itself as diagnosis or treatment. Talk to an informed adult and read credible evidence before you build anything.
                </p>
              )}
            </div>
            <ul className="lb-avoid__list">
              {extra.avoid.map((item) => (
                <li key={item}>
                  <Prohibit weight="bold" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="lb-role" aria-labelledby="lb-role-title">
          <div className="lb-role__head">
            <span className="lb-mono">GRID's part</span>
            <h2 id="lb-role-title">We help students make a bounded contribution.</h2>
            <p>GRID is a way into the work. It is not a substitute for communities, experts, public systems or long-term delivery.</p>
          </div>
          <ul className="lb-role__list">
            {problem.indirectHelp.map((step) => (
              <li key={step}><ShieldCheck weight="bold" aria-hidden="true" /><span>{step}</span></li>
            ))}
          </ul>
        </section>

        <section className="lb-readnext" aria-labelledby="lb-readnext-title">
          <h2 id="lb-readnext-title">Read before you start.</h2>
          <div className="lb-readnext__list">
            {readNext.map((guide) => (
              <Link key={guide.slug} to={`/guides/${guide.slug}`} className="lb-readnext__card">
                <GuideGlyph className="lb-readnext__glyph" variant={GUIDE_ART[guide.slug].variant} delay={0.1} onView />
                <span className="lb-mono">Guide · {guide.readTime}</span>
                <strong>{guide.title}</strong>
                <small>{guide.summary}</small>
              </Link>
            ))}
          </div>
          <nav className="lb-others" aria-label="Other problem areas">
            <span className="lb-mono">Other areas</span>
            <ul>
              {others.map((item) => (
                <li key={item.slug}>
                  <Link to={`/problems/${item.slug}`}>
                    <ProblemIcon name={PROBLEM_EXTRA[item.slug].icon} weight="bold" />
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </section>
      </main>
    </PageFrame>
  );
}

export default function ProblemPage() {
  const { category } = useParams();
  const problem = problemCategories.find((item) => item.slug === category);
  if (!problem) return <LearnNotFound kind="problem" />;
  return <ProblemStory key={problem.slug} problem={problem} />;
}
